const SESSION_STORAGE_KEY = 'pb26_session';

function resolveApiBase(): string {
  const configured = import.meta.env.VITE_API_URL?.trim() ?? '';
  if (!configured) return '';

  try {
    const apiHost = new URL(configured).hostname;
    const pageHost = window.location.hostname;
    const pageIsLocal =
      pageHost === 'localhost' || pageHost === '127.0.0.1' || pageHost === '[::1]';
    const apiIsLocal =
      apiHost === 'localhost' || apiHost === '127.0.0.1' || apiHost === '[::1]';

    // VK Tunnel / mobile WebView: page is on HTTPS tunnel, API must go via same origin (/api proxy).
    if (apiIsLocal && !pageIsLocal) return '';
  } catch {
    return configured;
  }

  return configured;
}

const API_BASE = resolveApiBase();

export function getSessionToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(SESSION_STORAGE_KEY, token);
    else sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* private mode / blocked storage */
  }
}

export class ApiError extends Error {
  registerUrl?: string;

  constructor(message: string, extras?: { registerUrl?: string }) {
    super(message);
    this.name = 'ApiError';
    this.registerUrl = extras?.registerUrl;
  }
}

function authHeaders(headers: Headers): Headers {
  const token = getSessionToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const body =
    options.body ??
    (method === 'POST' || method === 'PUT' || method === 'PATCH' ? '{}' : undefined);

  const headers = authHeaders(new Headers(options.headers));
  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    method,
    body,
    credentials: 'include',
    headers,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const payload = data as { error?: string; registerUrl?: string };
    throw new ApiError(payload.error ?? res.statusText, {
      registerUrl: payload.registerUrl,
    });
  }
  if (typeof (data as { token?: string }).token === 'string') {
    setSessionToken((data as { token: string }).token);
  }
  return data as T;
}

export async function apiDownload(path: string, filename: string): Promise<void> {
  const headers = authHeaders(new Headers());
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? res.statusText);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function apiUpload<T>(
  path: string,
  file: File,
): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const headers = authHeaders(new Headers());
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    body: form,
    credentials: 'include',
    headers,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error ?? res.statusText);
  }
  if (typeof (data as { token?: string }).token === 'string') {
    setSessionToken((data as { token: string }).token);
  }
  return data as T;
}
